import React, { useState, useEffect } from 'react';
import { Container, Form, Card, Button, Alert, Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { amazonAPI } from '../api/amazonAPI';
import { usePermissions } from '../contexts/PermissionContext';
import { Link } from 'react-router-dom';
import {
    formatDateTime, utcTimeToLocal, localTimeToUtc, HOUR_OPTIONS, MINUTE_OPTIONS,
} from '../utils/commonUtils';

const InfoTip = ({ id, children }) => (
    <OverlayTrigger
        placement="right"
        overlay={<Tooltip id={`tip-${id}`}>{children}</Tooltip>}
    >
        <i className="fas fa-info-circle text-muted ms-2" style={{ cursor: 'help', fontSize: '0.85em' }}></i>
    </OverlayTrigger>
);

const SCHEDULE_GROUPS = [
    { hourField: 'daily_collection_hour', minuteField: 'daily_collection_minute' },
    { hourField: 'weekly_collection_hour', minuteField: 'weekly_collection_minute', dayField: 'weekly_collection_day' },
    { hourField: 'cleanup_hour', minuteField: 'cleanup_minute', dayField: 'cleanup_day' },
    { hourField: 'monthly_collection_hour', minuteField: 'monthly_collection_minute' },
];

function convertScheduleFields(data, convertFn) {
    const result = { ...data };
    for (const g of SCHEDULE_GROUPS) {
        const converted = convertFn(data[g.hourField], data[g.minuteField], g.dayField ? data[g.dayField] : undefined);
        result[g.hourField] = converted.hour;
        result[g.minuteField] = converted.minute;
        if (g.dayField && converted.day !== undefined) result[g.dayField] = converted.day;
    }
    return result;
}

const DAYS_OF_WEEK = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' },
];

const ScheduleRow = ({ label, tipId, tipText, hourField, minuteField, dayField, config, onChange, disabled }) => (
    <Form.Group className="mb-3" style={{ opacity: disabled ? 0.5 : 1 }}>
        <Form.Label>
            {label}
            <InfoTip id={tipId}>{tipText}</InfoTip>
        </Form.Label>
        <Row className="g-2">
            {dayField && (
                <Col xs={4}>
                    <Form.Select
                        name={dayField}
                        value={config[dayField]}
                        onChange={onChange}
                        disabled={disabled}
                        size="sm"
                    >
                        {DAYS_OF_WEEK.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                    </Form.Select>
                </Col>
            )}
            <Col xs={dayField ? 4 : 6}>
                <Form.Select
                    name={hourField}
                    value={config[hourField]}
                    onChange={onChange}
                    disabled={disabled}
                    size="sm"
                >
                    {HOUR_OPTIONS.map(h => (
                        <option key={h.value} value={h.value}>{h.label}</option>
                    ))}
                </Form.Select>
            </Col>
            <Col xs={dayField ? 4 : 6}>
                <div className="d-flex align-items-center">
                    <span className="me-1">:</span>
                    <Form.Select
                        name={minuteField}
                        value={config[minuteField]}
                        onChange={onChange}
                        disabled={disabled}
                        size="sm"
                    >
                        {MINUTE_OPTIONS.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </Form.Select>
                </div>
            </Col>
        </Row>
    </Form.Group>
);

const REPORT_TYPE_LABELS = {
    ad_performance: 'Ad Performance',
    sp_campaigns: 'SP Campaigns',
    sponsored_brands: 'Sponsored Brands',
    sponsored_display: 'Sponsored Display',
    search_terms: 'Search Terms',
    economics: 'Economics',
    traffic: 'Traffic',
    inventory: 'FBA Inventory',
    planning: 'Inventory Planning',
    orders: 'Orders (FBA/FBM)',
};

const STATUS_BADGE_VARIANT = {
    success: 'success',
    failed: 'danger',
    pending: 'warning',
    disabled: 'secondary',
};

function formatAttemptMessage(entry) {
    if (!entry) return '';
    const { status, attempts } = entry;
    if (status === 'disabled') return 'Disabled';
    if (status === 'pending') return attempts > 1 ? `Pending (attempt ${attempts})` : 'Pending';
    if (status === 'success') {
        if (attempts <= 1) return 'Success';
        return `Success on attempt ${attempts}`;
    }
    if (status === 'failed') {
        return `Failed after ${attempts} attempt${attempts !== 1 ? 's' : ''}`;
    }
    return '';
}

function AmazonDataCollectionSettingsScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountPk, setSelectedAccountPk] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [alert, setAlert] = useState(null);

    const [reportSettings, setReportSettings] = useState(null);
    const [reportSettingsDirty, setReportSettingsDirty] = useState(false);
    const [reportSettingsSaving, setReportSettingsSaving] = useState(false);
    const [reportSettingsAlert, setReportSettingsAlert] = useState(null);

    useEffect(() => {
        loadAccounts();
        loadReportSettings();
    }, []);

    useEffect(() => {
        if (selectedAccountPk) {
            loadConfig(selectedAccountPk);
        }
    }, [selectedAccountPk]);

    const loadAccounts = async () => {
        try {
            setLoading(true);
            const data = await amazonAPI.accounts.getAll();
            if (data && data.length > 0) {
                setAccounts(data);
                setSelectedAccountPk(data[0].id);
            } else {
                setLoading(false);
            }
        } catch {
            setAlert({ variant: 'danger', message: 'Failed to load Amazon accounts' });
            setLoading(false);
        }
    };

    const loadConfig = async (accountPk) => {
        try {
            setLoading(true);
            const data = await amazonAPI.collectionConfig.get(accountPk);
            if (data) {
                setConfig(convertScheduleFields(data, utcTimeToLocal));
                setDirty(false);
                setAlert(null);
            }
        } catch {
            setAlert({ variant: 'danger', message: 'Failed to load collection config' });
        } finally {
            setLoading(false);
        }
    };

    const loadReportSettings = async () => {
        try {
            const data = await amazonAPI.reportSettings.get();
            setReportSettings(data);
        } catch {
            setReportSettingsAlert({ variant: 'danger', message: 'Failed to load report settings' });
        }
    };

    const handleReportSettingsChange = (e) => {
        const { name, value } = e.target;
        setReportSettings(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
        setReportSettingsDirty(true);
        setReportSettingsAlert(null);
    };

    const handleReportSettingsSave = async () => {
        setReportSettingsSaving(true);
        try {
            const data = await amazonAPI.reportSettings.update({
                units_sold_rolling_days: reportSettings.units_sold_rolling_days,
                sales_level_best_threshold: reportSettings.sales_level_best_threshold,
                sales_level_medium_threshold: reportSettings.sales_level_medium_threshold,
                inventory_low_stock_months: reportSettings.inventory_low_stock_months,
                max_collection_retries: reportSettings.max_collection_retries,
            });
            setReportSettings(data);
            setReportSettingsDirty(false);
            setReportSettingsAlert({ variant: 'success', message: 'Report settings saved successfully' });
        } catch {
            setReportSettingsAlert({ variant: 'danger', message: 'Failed to save report settings' });
        } finally {
            setReportSettingsSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const utcSchedule = convertScheduleFields(config, localTimeToUtc);
            const result = await amazonAPI.collectionConfig.update(selectedAccountPk, {
                is_enabled: config.is_enabled,
                collect_ad_performance: config.collect_ad_performance,
                collect_brand_display_ads: config.collect_brand_display_ads,
                collect_search_terms: config.collect_search_terms,
                collect_economics: config.collect_economics,
                collect_traffic: config.collect_traffic,
                collect_inventory: config.collect_inventory,
                collect_inventory_planning: config.collect_inventory_planning,
                collect_orders: config.collect_orders,
                daily_lookback_days: config.daily_lookback_days,
                weekly_lookback_days: config.weekly_lookback_days,
                search_term_lookback_days: config.search_term_lookback_days,
                daily_retention_months: config.daily_retention_months,
                collection_metadata_retention_days: config.collection_metadata_retention_days,
                daily_collection_hour: utcSchedule.daily_collection_hour,
                daily_collection_minute: utcSchedule.daily_collection_minute,
                weekly_collection_hour: utcSchedule.weekly_collection_hour,
                weekly_collection_minute: utcSchedule.weekly_collection_minute,
                weekly_collection_day: utcSchedule.weekly_collection_day,
                cleanup_hour: utcSchedule.cleanup_hour,
                cleanup_minute: utcSchedule.cleanup_minute,
                cleanup_day: utcSchedule.cleanup_day,
                monthly_collection_enabled: config.monthly_collection_enabled,
                monthly_settling_days: config.monthly_settling_days,
                monthly_collection_hour: utcSchedule.monthly_collection_hour,
                monthly_collection_minute: utcSchedule.monthly_collection_minute,
            });
            if (result) {
                setConfig(convertScheduleFields(result, utcTimeToLocal));
                setDirty(false);
                setAlert({ variant: 'success', message: 'Settings saved successfully' });
            }
        } catch {
            setAlert({ variant: 'danger', message: 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setConfig(prev => ({ ...prev, [name]: checked }));
        setDirty(true);
        setAlert(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
        setDirty(true);
        setAlert(null);
    };

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    if (!hasPermission('amazon.manage')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to manage Amazon settings.
                </Alert>
            </Container>
        );
    }

    if (accounts.length === 0) {
        return (
            <Container className="py-3">
                <h1 className="mb-4">Amazon Data Collection Settings</h1>
                <Alert variant="info">
                    No Amazon accounts configured. Create an account first in Settings &rarr; Amazon Accounts.
                </Alert>
            </Container>
        );
    }

    if (!config) {
        return (
            <Container className="py-3">
                <h1 className="mb-4">Amazon Data Collection Settings</h1>
                <Alert variant="danger">Failed to load configuration.</Alert>
            </Container>
        );
    }

    return (
        <Container className="py-3">
            <h1 className="mb-4">Amazon Data Collection Settings</h1>

            {reportSettings && (
                <Row className="justify-content-left mb-4">
                    <Col style={{ maxWidth: '730px' }}>
                        <Card>
                            <Card.Header>Report Settings (Global)</Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Units Sold Rolling Window (days)
                                                <InfoTip id="rolling-days">Number of rolling days used to calculate Units Sold on the Daily Dashboard. For example, 30 means the dashboard shows total units sold over the last 30 days.</InfoTip>
                                            </Form.Label>
                                            <Form.Text className="d-block mb-1 text-body-secondary">
                                                Also the window for sale price and fees in break-even ACoS, used by Anomaly Detection for all accounts.
                                            </Form.Text>
                                            <Form.Control
                                                type="number"
                                                name="units_sold_rolling_days"
                                                value={reportSettings.units_sold_rolling_days}
                                                onChange={handleReportSettingsChange}
                                                min={1}
                                                max={365}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Low Stock Threshold (months supply)
                                                <InfoTip id="low-stock">ASINs with fewer than this many months of supply are flagged as &quot;Low stock&quot; on the Daily Dashboard and reports.</InfoTip>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="inventory_low_stock_months"
                                                value={reportSettings.inventory_low_stock_months}
                                                onChange={handleReportSettingsChange}
                                                min={1}
                                                max={24}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Best Seller Threshold (units/month)
                                                <InfoTip id="best-threshold">ASINs selling at or above this monthly unit rate are classified as &quot;Best Seller&quot;. The rate is extrapolated from the rolling window above.</InfoTip>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="sales_level_best_threshold"
                                                value={reportSettings.sales_level_best_threshold}
                                                onChange={handleReportSettingsChange}
                                                min={1}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Medium Seller Threshold (units/month)
                                                <InfoTip id="medium-threshold">ASINs selling at or above this monthly unit rate (but below the Best Seller threshold) are classified as &quot;Medium Seller&quot;. Below this threshold is &quot;Slow Seller&quot;.</InfoTip>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="sales_level_medium_threshold"
                                                value={reportSettings.sales_level_medium_threshold}
                                                onChange={handleReportSettingsChange}
                                                min={1}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Max Collection Retries
                                                <InfoTip id="max-retries">Maximum number of retry attempts for each report type during automated collection. Applies to both submission failures and stuck reports that time out.</InfoTip>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="max_collection_retries"
                                                value={reportSettings.max_collection_retries}
                                                onChange={handleReportSettingsChange}
                                                min={1}
                                                max={10}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {reportSettingsAlert && (
                                    <Alert
                                        variant={reportSettingsAlert.variant}
                                        onClose={() => setReportSettingsAlert(null)}
                                        dismissible
                                        className="mb-3"
                                    >
                                        {reportSettingsAlert.message}
                                    </Alert>
                                )}
                                <Button
                                    variant="primary"
                                    onClick={handleReportSettingsSave}
                                    disabled={reportSettingsSaving || !reportSettingsDirty}
                                >
                                    {reportSettingsSaving ? 'Saving...' : 'Save Report Settings'}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {accounts.length > 1 && (
                <Form.Group className="mb-4">
                    <Form.Label>Amazon Account</Form.Label>
                    <Form.Select
                        value={selectedAccountPk || ''}
                        onChange={(e) => setSelectedAccountPk(parseInt(e.target.value, 10))}
                    >
                        {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}

            <Form onSubmit={handleSubmit}>
                <Row className="justify-content-left">
                    <Col style={{ maxWidth: '730px' }}>
                        <Card className="mb-4">
                            <Card.Header>Data Collection</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="is_enabled"
                                        name="is_enabled"
                                        label={<span>Enable Automated Collection<InfoTip id="enabled">Master switch for all automated data collection. When disabled, no scheduled tasks will run. The Celery Beat schedules must also be enabled in Django Admin for tasks to execute.</InfoTip></span>}
                                        checked={config.is_enabled}
                                        onChange={handleCheckboxChange}
                                    />
                                    <Form.Text className="text-muted">
                                        When enabled, the system will automatically collect data
                                        from Amazon on a daily schedule.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Advertising Reports</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_ad_performance"
                                        name="collect_ad_performance"
                                        label={<span>Collect Ad Performance Reports<InfoTip id="ad-perf">Collects ASIN-level metrics: impressions, clicks, spend, and attributed sales. Data is aggregated per ASIN per day and stored with daily granularity. Each collection overwrites previous data for the same date, keeping the most accurate attribution.</InfoTip></span>}
                                        checked={config.collect_ad_performance}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Sponsored Products advertised-product reports (ASIN-level impressions,
                                        clicks, spend, sales).
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_brand_display_ads"
                                        name="collect_brand_display_ads"
                                        label={<span>Collect Sponsored Brands / Display Spend<InfoTip id="brand-display">Collects campaign-level spend and attributed sales for Sponsored Brands and Sponsored Display. These ad products cannot be tied to a single ASIN, so the data is used at account level only, in the Monthly Profitability account summary.</InfoTip></span>}
                                        checked={!!config.collect_brand_display_ads}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Sponsored Brands and Sponsored Display campaign reports (account-level spend,
                                        clicks, sales). Daily, weekly sweep and settled monthly report.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_search_terms"
                                        name="collect_search_terms"
                                        label={<span>Collect Search Term Reports<InfoTip id="search-terms">Collects customer search queries that triggered your ads. Useful for discovering new keywords and identifying negative targeting opportunities. Only collected during daily runs, not weekly sweeps.</InfoTip></span>}
                                        checked={config.collect_search_terms}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Search term reports for keyword optimization and negative targeting.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Data Sources</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_orders"
                                        name="collect_orders"
                                        label={<span>Collect Orders (FBA / FBM split)<InfoTip id="orders">Collects the All Orders report for the last 7 days each night: ordered units and product sales per SKU per day, split by fulfillment channel (Amazon vs Seller). Powers the fulfillment channel selector on the Daily Dashboard and Monthly Profitability, matching the Seller Central Sales Dashboard.</InfoTip></span>}
                                        checked={!!config.collect_orders}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        SP-API All Orders report: FBA vs FBM units and revenue per SKU per day.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_economics"
                                        name="collect_economics"
                                        label={<span>Collect Economics Data<InfoTip id="economics">Collects daily revenue, fees, COGS, and profit data per SKU via DataKiosk. Used for Monthly Profitability Reports and Daily Dashboard. Each day&apos;s data is stored separately and aggregates naturally into monthly/arbitrary periods.</InfoTip></span>}
                                        checked={config.collect_economics}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        DataKiosk economics: revenue, fees, COGS, and profit per SKU.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_traffic"
                                        name="collect_traffic"
                                        label={<span>Collect Traffic Data<InfoTip id="traffic">Collects daily sessions, page views, buy box percentage, and conversion data per ASIN via DataKiosk. Used for Daily Dashboard and traffic trend analysis.</InfoTip></span>}
                                        checked={config.collect_traffic}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        DataKiosk traffic: sessions, page views, buy box %, conversion rates.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_inventory"
                                        name="collect_inventory"
                                        label={<span>Collect FBA Inventory<InfoTip id="inventory">Collects a daily snapshot of FBA inventory levels per SKU. Used for Daily Dashboard inventory column and inventory trend analysis. The latest snapshot shows current stock levels.</InfoTip></span>}
                                        checked={config.collect_inventory}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        FBA inventory snapshot: stock levels per SKU.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="collect_inventory_planning"
                                        name="collect_inventory_planning"
                                        label={<span>Collect Inventory Planning<InfoTip id="inv-planning">Collects FBA inventory planning data: health status, days of supply, recommended actions, and low inventory fee indicators per SKU. Used for proactive inventory management.</InfoTip></span>}
                                        checked={config.collect_inventory_planning}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Inventory planning: health status, days of supply, recommended actions.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Monthly Collection</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled ? 1 : 0.5 }}>
                                    <Form.Check
                                        type="switch"
                                        id="monthly_collection_enabled"
                                        name="monthly_collection_enabled"
                                        label={<span>Enable Monthly Data Collection<InfoTip id="monthly-enabled">When enabled, the system will request full-month data from Amazon after the settling period. Monthly data provides complete, finalized fee information &mdash; more accurate than aggregating daily records.</InfoTip></span>}
                                        checked={config.monthly_collection_enabled}
                                        onChange={handleCheckboxChange}
                                        disabled={!config.is_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Automatically collect full-month economics, traffic, and ad
                                        data after the settling period.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ opacity: config.is_enabled && config.monthly_collection_enabled ? 1 : 0.5 }}>
                                    <Form.Label>
                                        Settling Period (days)
                                        <InfoTip id="settling-days">Days to wait after the end of a month before requesting the monthly data. Amazon needs time to finalize fees, attribution, and adjustments. Default 15 days works well for most cases.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="monthly_settling_days"
                                        value={config.monthly_settling_days}
                                        onChange={handleInputChange}
                                        min={5}
                                        max={30}
                                        disabled={!config.is_enabled || !config.monthly_collection_enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        Wait this many days into the next month before collecting.
                                        For example, July data is collected on August 16 with a 15-day
                                        settling period.
                                    </Form.Text>
                                </Form.Group>
                                <ScheduleRow
                                    label="Monthly Collection Time"
                                    tipId="sched-monthly"
                                    tipText="The daily check time for monthly collection. The task runs every day but only submits queries when the settling period has elapsed for the previous month."
                                    hourField="monthly_collection_hour"
                                    minuteField="monthly_collection_minute"
                                    config={config}
                                    onChange={handleInputChange}
                                    disabled={!config.is_enabled || !config.monthly_collection_enabled}
                                />
                                {config.last_monthly_run_at && (
                                    <div className="mt-2">
                                        <Form.Label className="text-muted small">Last Monthly Run</Form.Label>
                                        <div>{formatDateTime(config.last_monthly_run_at)}</div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Automations</Card.Header>
                            <Card.Body>
                                <span className="text-body-secondary">
                                    Daily automation runs (on/off and run time) are set in{' '}
                                    <Link to={`/amazon-ad-automation/settings?tab=general${selectedAccountPk ? `&account=${selectedAccountPk}` : ''}`}>
                                        Ad Automation Settings → General
                                    </Link>.
                                </span>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Schedules</Card.Header>
                            <Card.Body>
                                <ScheduleRow
                                    label="Daily Collection"
                                    tipId="sched-daily"
                                    tipText="When the daily ad data collection runs. Pulls data from T-N to T-1 (where N is the Daily Lookback). Runs every day at the specified time. Recommended: early morning before the team starts reviewing data."
                                    hourField="daily_collection_hour"
                                    minuteField="daily_collection_minute"
                                    config={config}
                                    onChange={handleInputChange}
                                    disabled={!config.is_enabled}
                                />
                                <ScheduleRow
                                    label="Weekly Restatement Sweep"
                                    tipId="sched-weekly"
                                    tipText="When the weekly restatement sweep runs. Re-pulls older data (from Daily Lookback to Weekly Lookback days back) to capture late Amazon attribution corrections. Runs once per week on the specified day."
                                    hourField="weekly_collection_hour"
                                    minuteField="weekly_collection_minute"
                                    dayField="weekly_collection_day"
                                    config={config}
                                    onChange={handleInputChange}
                                    disabled={!config.is_enabled}
                                />
                                <ScheduleRow
                                    label="Data Cleanup"
                                    tipId="sched-cleanup"
                                    tipText="When the cleanup task runs. Aggregates daily data older than the retention period into monthly summaries, then deletes the daily records and old collection metadata. Runs once per week on the specified day."
                                    hourField="cleanup_hour"
                                    minuteField="cleanup_minute"
                                    dayField="cleanup_day"
                                    config={config}
                                    onChange={handleInputChange}
                                    disabled={!config.is_enabled}
                                />
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Lookback Windows</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Daily Lookback (days)
                                        <InfoTip id="daily-lookback">How many days back the daily collection pulls. Amazon ad attribution takes up to 14 days for Sponsored Products (7 days for Sponsored Brands). Setting this to 14 ensures each day&apos;s data is re-collected until attribution is fully settled. Lower values save API calls but may miss late-attributed conversions.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="daily_lookback_days"
                                        value={config.daily_lookback_days}
                                        onChange={handleInputChange}
                                        min={1}
                                        max={60}
                                    />
                                    <Form.Text className="text-muted">
                                        Daily pull range: T-N to T-1. Default 14 covers the full
                                        Sponsored Products attribution window.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Weekly Lookback (days)
                                        <InfoTip id="weekly-lookback">Weekly restatement sweep catches Amazon&apos;s late corrections beyond the daily window. Amazon may adjust attribution data for up to 30 days after the click. This runs once per week and covers dates between the daily lookback and this value. Should be greater than Daily Lookback.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="weekly_lookback_days"
                                        value={config.weekly_lookback_days}
                                        onChange={handleInputChange}
                                        min={1}
                                        max={90}
                                    />
                                    <Form.Text className="text-muted">
                                        Weekly restatement sweep range. Catches Amazon corrections
                                        beyond the daily window.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Search Term Lookback (days)
                                        <InfoTip id="search-lookback">Search term data has much higher volume than ASIN-level performance data (thousands of unique search queries). A shorter lookback window keeps the API request size manageable and avoids rate limiting. Recent search terms are most actionable for keyword optimization.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="search_term_lookback_days"
                                        value={config.search_term_lookback_days}
                                        onChange={handleInputChange}
                                        min={1}
                                        max={30}
                                    />
                                    <Form.Text className="text-muted">
                                        Shorter than ad performance lookback due to higher data volume.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Retention</Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Daily Data Retention (months)
                                        <InfoTip id="daily-retention">After this period, daily granularity data is automatically aggregated into monthly summaries and the daily records are deleted. Monthly summaries are kept indefinitely for year-over-year comparison. Default 24 months means you can analyze daily trends for the past 2 years, with monthly data available beyond that.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="daily_retention_months"
                                        value={config.daily_retention_months}
                                        onChange={handleInputChange}
                                        min={1}
                                        max={120}
                                    />
                                    <Form.Text className="text-muted">
                                        Daily granularity data is kept for this period, then aggregated
                                        into monthly summaries for year-over-year comparison.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Collection Metadata Retention (days)
                                        <InfoTip id="metadata-retention">Report metadata includes submission timestamps, status, and configuration details for each automated collection run. This is housekeeping data used for debugging. On-demand reports (manually generated from the UI) are never affected by this cleanup.</InfoTip>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="collection_metadata_retention_days"
                                        value={config.collection_metadata_retention_days}
                                        onChange={handleInputChange}
                                        min={1}
                                        max={365}
                                    />
                                    <Form.Text className="text-muted">
                                        Report metadata from collection tasks. On-demand reports
                                        are not affected.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header>Collection Status</Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label className="text-muted small">Last Daily Run</Form.Label>
                                        <div>{formatDateTime(config.last_daily_run_at) || 'Never'}</div>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label className="text-muted small">Last Weekly Run</Form.Label>
                                        <div>{formatDateTime(config.last_weekly_run_at) || 'Never'}</div>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label className="text-muted small">Last Cleanup</Form.Label>
                                        <div>{formatDateTime(config.last_cleanup_at) || 'Never'}</div>
                                    </Col>
                                </Row>
                                {config.last_collection_status && Object.keys(config.last_collection_status).length > 0 ? (
                                    <>
                                        <hr />
                                        <Form.Label className="text-muted small mb-2">Report Status (Last Run)</Form.Label>
                                        {Object.keys(REPORT_TYPE_LABELS).map(key => {
                                            const entry = config.last_collection_status[key];
                                            if (!entry) return null;
                                            const variant = STATUS_BADGE_VARIANT[entry.status] || 'secondary';
                                            return (
                                                <div key={key} className="d-flex align-items-start mb-2">
                                                    <div style={{ minWidth: '150px' }} className="fw-semibold small">{REPORT_TYPE_LABELS[key]}</div>
                                                    <div>
                                                        <Badge bg={variant} className="me-2">{entry.status}</Badge>
                                                        <span className="small">{formatAttemptMessage(entry)}</span>
                                                        {entry.updated_at && (
                                                            <span className="text-muted small ms-2">{formatDateTime(entry.updated_at)}</span>
                                                        )}
                                                        {entry.status === 'failed' && entry.error && (
                                                            <div className="text-danger small mt-1">{entry.error}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <>
                                        <hr />
                                        <div className="text-muted small">No detailed report status available yet. Status will appear after the next collection run.</div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>

                        {alert && (
                            <Alert
                                variant={alert.variant}
                                onClose={() => setAlert(null)}
                                dismissible
                            >
                                {alert.message}
                            </Alert>
                        )}

                        <Button type="submit" variant="primary" disabled={saving || !dirty}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
}

export default AmazonDataCollectionSettingsScreen;
